import React, { useEffect, ClipboardEvent } from "react";
import styled from "@emotion/styled"

import ContentEditable from "react-contenteditable";
import { Grid, Stack, styled as MUIStyled, TooltipProps, Tooltip, tooltipClasses } from "@mui/material";

import { RootState } from "../../../../redux/store";
import { useDispatch, useSelector } from "react-redux";
import { capitalizeSentences, ChannelInfo, convertToDivs, getChannelId, replaceDetails, returnSingularOrPlural } from "../../../../utils/helpers";
import { INFO_CIRCLE } from "../../../../constants/icons/InfoCircle";
import { PreviewType } from "../../../../types/dataTypes";
import { useGetContactableMailCountQuery, usePostAiEmailMutation, usePostRephraseEmailMutation } from "../../../../redux/services/folderName";
import { setContentPersonalisationGenratedViaAI, setPreviewNo, setPreviews } from "../../../../redux/slice/campaignSlice";
import { LEFT_DISABLED, LEFT_DISABLED_SMALL, LEFT_ENABLE, LEFT_ENABLE_SMALL, RIGHT_DISABLED, RIGHT_DISABLED_SMALL, RIGHT_ENABLE, RIGHT_ENABLE_SMALL } from "../../../../constants/icons/ArrowIcon";
import { COLOR_MAP } from "../../../../constants/ColorNames";

type EmailContentProps = {
    handleContentChange: (event: React.BaseSyntheticEvent, element: string) => void,
    email: string,
    setActiveElement: (event: any, element: string) => void,
    showError?: boolean,
    alreadyGenerated: boolean,
    alreadyRephrased: boolean,
    revealingContent: boolean,
    revealingContentTab: string,
    revealedEmail: string,
    candidateFullName: string
    handlePaste: (event: React.ClipboardEvent<HTMLDivElement>) => void;
}
const Container = styled(Grid)`
    font-size: 1.2rem;
    font-weight: 400;
    line-height: 2rem;
`
const PreviewContainer = styled(Stack)`
    border-radius: 0 34px 34px 8px;
    border: 1px solid #80BDFF;
    background: #EAF4FF;
    width: fit-content;
    align-items: center;
    gap: 24px;
    padding: 8px;
    height: 16px;
`
const BootstrapTooltip = MUIStyled(({ className, ...props }: TooltipProps) => (
    <Tooltip {...props} arrow classes={{ popper: className }} />
))(({ theme }) => ({
    [`& .${tooltipClasses.arrow}`]: {
        color: theme.palette.common.black,
    },
    [`& .${tooltipClasses.tooltip}`]: {
        backgroundColor: theme.palette.common.black,
    }
}));

export type Ref = React.MutableRefObject<HTMLInputElement>;
const EmailContent = React.forwardRef<HTMLInputElement, EmailContentProps>((props, ref) => {

    const { handleContentChange,handlePaste, candidateFullName,email, setActiveElement, showError = false, alreadyGenerated, alreadyRephrased, revealingContent, revealingContentTab, revealedEmail } = props;

    const jobTitle = useSelector((state: RootState) => state.campaign.jobTitle);
    const minExperience = useSelector((state: RootState) => state.campaign.minExperience);
    const maxExperience = useSelector((state: RootState) => state.campaign.maxExperience);
    const selectedTab = useSelector((state: RootState) => state.campaign.selectedTab);
    const previews = useSelector((state: RootState) => state.campaign.previewState);
    const previewNo = useSelector((state: RootState) => state.campaign.previewNo);
    const { campaignInitiationId } = useSelector((state: RootState) => state.campaign.campaignCreationQueryData);
    const { data: contactableMailDetails } = useGetContactableMailCountQuery(campaignInitiationId, {skip : !campaignInitiationId});
    const contactableCount = contactableMailDetails?.contactableCount || 1;
    const { contentPersonalisationGenratedViaAI, contentPersonalisationType } = useSelector((state: RootState) => state.campaign);
    const [postAiEmail] = usePostAiEmailMutation();
    const [postRephraseEmail] = usePostRephraseEmailMutation();


    const dispatch = useDispatch()

    const content = replaceDetails(email, jobTitle, minExperience, maxExperience,candidateFullName) 
    
    const searchParams = new URLSearchParams(window.location.search);

    const cn = searchParams.get('cn') as string
    const { domain } : ChannelInfo = getChannelId(cn)

    const returnContent = () => {
        let content: string;
        if (previewNo !== 1 && selectedTab == "Email") {
            content = previews?.find((i: PreviewType) => i.previewNumber === previewNo)?.email || ''
        }
        else {
            content = email
        }
        return replaceDetails(content, jobTitle, minExperience, maxExperience,candidateFullName) 
    }

    const htmlContent = revealingContent && (revealingContentTab === selectedTab) ? revealedEmail : returnContent();

    // Function to set the text color of <font> tags to black
    const setFontColorToBlack = () => {
        const fontElements = document.querySelectorAll('font');
        fontElements.forEach((fontElement) => {
            const element = fontElement as HTMLElement; // Cast to HTMLElement
            element.style.color = 'black';
        });
    }

    useEffect(() => {
        setFontColorToBlack(); // Apply the black text color to <font> tags
    }, [content]);

    // const handlePaste = (e: ClipboardEvent) => {
    //   e.preventDefault();
    //   const isHTML = e.clipboardData.getData("text/html") ? true : false;
    //   const pastedContent = isHTML
    //     ? e.clipboardData.getData("text/html")
    //     : e.clipboardData.getData("text/plain");


    //   let formattedPastedContent;
    //   if (isHTML) {
    //     // Create a temporary element to parse the pasted content as HTML
    //     const tempDiv = document.createElement("div");
    //     tempDiv.innerHTML = pastedContent;

    //     // Call the function to start conversion
    //     convertToDivs(tempDiv);
    //     formattedPastedContent = tempDiv.innerHTML;
    //   }
    //   else {
    //     formattedPastedContent = pastedContent
    //       .split('\n')
    //       .map(line => `<span style="font-size: 1.4rem; line-height: 3rem;">${capitalizeSentences(line)}</span>`)
    //       .join('');
    //   }

    //   // Insert the formatted content into the document
    //   const selection = window.getSelection();
    //   if (selection) {
    //     const range = selection.getRangeAt(0);
    //     range.deleteContents();
    //     const fragment = range.createContextualFragment(formattedPastedContent);
    //     range.insertNode(fragment);
    //   }
    // };


    const handlePreviewNavigate = (direction: 'prev' | 'next') => {
        if (direction === 'prev') {
            if (revealingContent || (previewNo === 1)) return
            dispatch(setPreviewNo(previewNo - 1))
        }
        else {

            // if (previewNo === contactableCount) return;
            // if ((revealingContent || previewNo === Math.min(contactableCount, 4) || !contentPersonalisationGenratedViaAI)) return;
            if ((revealingContent || previewNo === contactableCount || !contentPersonalisationGenratedViaAI)) return;
            if (previews?.filter(i => i.previewNumber === previewNo + 1).length) { dispatch(setPreviewNo(previewNo + 1)); return; }
            if (contentPersonalisationType) {
                handlePreviewWithoutAI()
            } else {
                handlePreviewWithAI()
            }
        }
    }

    const handlePreviewWithAI = () => {
        postAiEmail({
            campaignInitiationId,
            previewProfileNo: previewNo + 1,
            isInitiationRequest: false
        }).unwrap()
            .then((res: { mailContent: { salutation: string; body: string; }; }) => {
                dispatch(setContentPersonalisationGenratedViaAI({ contentPersonalisationGenratedViaAI: 1 }))
                if (res) {
                    const msg = res?.mailContent?.salutation + "<br>" + res?.mailContent?.body?.replaceAll("\\n", "<br>");
                    dispatch(setPreviews({ previewState: [...previews, { email: msg, previewNumber: previewNo + 1 }] }))
                    // setPreviews([...previews, { email: msg, previewNumber: previewNo + 1 }])
                    dispatch(setPreviewNo(previewNo + 1))
                }
            })
            .catch((err: any) => {
                console.log(err);
            })
    }

    const handlePreviewWithoutAI = () => {
        postRephraseEmail(
        {
            payload: {
            alreadyViewedKeys: [],
            campaignInitiationId,
            followUp: 0,
            type: "REPHRASE",
            previewProfileNo: previewNo + 1,
            isInitiationRequest: false
        },
        domain
    })
            .unwrap()
            .then((res: { mailContent: { salutation: string; body: string; }; }) => {
                dispatch(setContentPersonalisationGenratedViaAI({ contentPersonalisationGenratedViaAI: 1 }))
                if (res) {
                    const msg = res?.mailContent?.salutation + "<br>" + res?.mailContent?.body?.replaceAll("\\n", "<br>");
                    dispatch(setPreviews({ previewState: [...previews, { email: msg, previewNumber: previewNo + 1 }] }))
                    // setPreviews([...previews, { email: msg, previewNumber: previewNo + 1 }])
                    dispatch(setPreviewNo(previewNo + 1))
                }
            })
            .catch((err: any) => {
                console.log(err);
            })
    }

const isFolllowUpTab = selectedTab.includes('Follow')
    return (<Container item padding='2rem 1.4rem' style={{
        width: '100%',
        flexGrow: 1
    }}>
        <div style={{
            width: '100%',
            // height: '100%',
            fontSize: '1.4rem',
            // lineHeight: '3rem',
            lineHeight: '2.2rem',
            // minHeight: '20rem',
            borderRadius: '0 0 0 8px',
            borderLeft: ((alreadyGenerated || alreadyRephrased) && !isFolllowUpTab) ? 'solid 1px #80BDFF' : 'none'
        }}>
            <ContentEditable
                style={{
                    width: '100%',
                    outline: 'none',
                    height: '100%',
                    // fontSize: '1.4rem',
                    // lineHeight: '3rem',
                    minHeight: '20rem',
                    borderRadius: '8px',
                    color: ((alreadyGenerated || alreadyRephrased) && !isFolllowUpTab) ? COLOR_MAP.GREY_DARK : COLOR_MAP.LIGHT_GREY,
                    padding: '12px 16px',
                    boxSizing: 'border-box'
                }}
                spellCheck={false}
                innerRef={ref!}
                html={htmlContent}
                disabled={(alreadyGenerated || alreadyRephrased) && !isFolllowUpTab}
                onChange={(e: React.BaseSyntheticEvent) => handleContentChange(e, 'email')}
                onClick={(e: any) => setActiveElement(e, 'email')}
                onBlur={(e)=> null}
                onPaste={handlePaste}
            />
            {(alreadyGenerated || alreadyRephrased) && !isFolllowUpTab && (
                <PreviewContainer direction="row" justifyContent="space-between">
                    <Stack direction="row" alignItems="center" className="message" gap="4px" sx={{ fontSize: '12px !important', lineHeight: '16px !important' }}>
                        <BootstrapTooltip placement="bottom-start" title={
                            <div style={{ width: '470px', padding: '4px 8px', fontSize: '14px', lineHeight: '20px' }}>
                                AI generated content which will be personalised for each candidate. Hence this cannot be edited. If you are not happy with the content you can rephrase or reset to choose other formats from templates
                            </div>
                        }>
                            <div style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}><INFO_CIRCLE /></div>
                        </BootstrapTooltip>
                        <span>This section is auto-personalized for each candidate and cannot be edited.</span>
                    </Stack>
                    {(selectedTab == 'Email') && (
                        <div style={{ display: 'flex', flexDirection: 'row', alignItems: 'center', gap: '4px' }}>
                            <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={() => handlePreviewNavigate('prev')}>
                                {(revealingContent || (previewNo === 1)) ? LEFT_DISABLED_SMALL : LEFT_ENABLE_SMALL}
                            </div>
                            <div style={{ color: COLOR_MAP.GREY_5, fontSize: '12px', lineHeight: '16px' }}>Messages<span style={{ color: COLOR_MAP.GREY_10, marginLeft: '4px' }}>{previewNo}</span>/{contactableCount}</div>
                            <div style={{ cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={() => handlePreviewNavigate('next')}>
                                {(revealingContent || (previewNo === contactableCount)) ? RIGHT_DISABLED_SMALL : RIGHT_ENABLE_SMALL}
                            </div>
                        </div>
                    )}
                </PreviewContainer>
            )}
        </div>

    </Container>)
})

export default EmailContent
